/**
 * The last few failed API calls, kept in memory so a quick feedback note can say what had just gone wrong
 * (feedback quick capture spec 01 §2).
 *
 * Method, path template, status and trace id only: never a body, and never a query string (it can carry search
 * text). Side-effect free and DOM free, like `providers.ts`: `client.ts` writes to it, the feedback panel reads it.
 */

export interface ApiFailure {
  method: string;
  /** The path with its ids replaced: `/orders/{id}/lines`. */
  path: string;
  /** The HTTP status, or 0 when no answer came (offline, refused, CORS). */
  status: number;
  /** The trace id the API answered with, when it sent one. */
  traceId: string | null;
}

/** "The last few": enough to see a pattern, few enough to read on the record. */
export const RECENT_API_FAILURES_KEPT = 5;

const recent: ApiFailure[] = [];

/**
 * A request path with its query and fragment removed and every segment that holds a digit (a uuid, `ORD-K48193`, a
 * page number) replaced by `{id}`, so the template names the route and not the record.
 */
export function templatePath(path: string): string {
  const withoutQuery = path.split(/[?#]/, 1)[0] ?? "";
  const segments = withoutQuery
    .split("/")
    .map((segment) => (/\d/.test(segment) ? "{id}" : segment));
  const joined = segments.join("/");
  return joined.startsWith("/") ? joined : `/${joined}`;
}

/** Remember one failed call, dropping the oldest beyond `RECENT_API_FAILURES_KEPT`. */
export function recordApiFailure(failure: {
  method: string;
  path: string;
  status: number;
  traceId?: string | null;
}): void {
  recent.push({
    method: failure.method.toUpperCase(),
    path: templatePath(failure.path),
    status: failure.status,
    traceId: failure.traceId?.trim() || null,
  });
  if (recent.length > RECENT_API_FAILURES_KEPT) recent.shift();
}

/** The remembered failures, oldest first. */
export function getRecentApiFailures(): readonly ApiFailure[] {
  return [...recent];
}

/** Forget them all (tests). */
export function clearRecentApiFailures(): void {
  recent.length = 0;
}
